import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mockSuppliers, mockSupplierPurchases } from '@/mocks/tr/suppliers';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Bekliyor': 'bg-gold-50 text-gold-600 border-gold-200',
    'Kısmi Ödendi': 'bg-blue-50 text-blue-600 border-blue-200',
    'Gecikti': 'bg-red-50 text-red-600 border-red-200',
    'Kapandı': 'bg-green-50 text-green-600 border-green-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'Gecikti' ? 'bg-red-500' : status === 'Kapandı' ? 'bg-green-500' : 'bg-gold-500'}`} />
      {status}
    </span>
  );
}

export default function Suppliers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<string | null>(null);

  const filteredPurchases = useMemo(() => {
    return mockSupplierPurchases.filter((p) => {
      const supplier = mockSuppliers.find((s) => s.id === p.supplier_id);
      return (
        supplier?.name.toLowerCase().includes(search.toLowerCase()) ||
        p.product_description.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [search]);

  const purchase = mockSupplierPurchases.find((p) => p.id === selectedPurchase);
  const supplier = purchase ? mockSuppliers.find((s) => s.id === purchase.supplier_id) : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('suppliers.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Tedarikçilerden alınan malları ve açık hesapları takip edin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t('suppliers.newPurchase')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white p-4 rounded-xl border border-cream-200">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-boutique-warm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-cream-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Tedarikçi</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Telefon</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Alım Tarihi</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ürün</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Toplam</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ödenen</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Kalan</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Vade</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Durum</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {filteredPurchases.map((p) => {
                const sup = mockSuppliers.find((s) => s.id === p.supplier_id);
                return (
                  <tr key={p.id} className="hover:bg-cream-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-boutique-black">{sup?.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{sup?.phone}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{p.purchase_date}</td>
                    <td className="px-4 py-3 text-boutique-warm max-w-xs truncate">{p.product_description}</td>
                    <td className="px-4 py-3 text-right font-semibold text-boutique-black whitespace-nowrap">{formatCurrency(p.total_amount)}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium whitespace-nowrap">{formatCurrency(p.paid_amount)}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium whitespace-nowrap">{formatCurrency(p.remaining_amount)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{p.due_date}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {p.remaining_amount > 0 && (
                          <button
                            onClick={() => { setSelectedPurchase(p.id); setShowPaymentModal(true); }}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-green-600 hover:bg-green-50 transition-colors"
                            title={t('common.payment')}
                          >
                            <i className="ri-money-cny-circle-line text-base" />
                          </button>
                        )}
                        <button className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors" title={t('common.detail')}>
                          <i className="ri-eye-line text-base" />
                        </button>
                        <button className="w-8 h-8 rounded-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title={t('common.edit')}>
                          <i className="ri-edit-line text-base" />
                        </button>
                        <button className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors" title={t('common.delete')}>
                          <i className="ri-delete-bin-line text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredPurchases.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-truck-line text-4xl text-cream-300 mb-3" />
            <p className="text-sm text-boutique-warm">{t('common.noData')}</p>
          </div>
        )}
      </div>

      {/* New Purchase Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('suppliers.newPurchase')}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Tedarikçi Adı</label>
                  <input className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="Tedarikçi adı" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Telefon</label>
                  <input className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="Telefon" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Alım Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ürün Açıklaması</label>
                <textarea className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm resize-none h-20" placeholder="Alınan ürünlerin açıklaması" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Toplam Tutar</label>
                  <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Ödenen Tutar</label>
                  <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Vade Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && purchase && supplier && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('common.payment')}</h2>
              <button onClick={() => setShowPaymentModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-cream-50 rounded-lg p-4">
                <p className="text-sm text-boutique-warm">Tedarikçi</p>
                <p className="font-medium text-boutique-black">{supplier.name}</p>
                <p className="text-sm text-boutique-warm">{purchase.product_description}</p>
                <p className="text-sm text-red-600 font-medium mt-1">Kalan: {formatCurrency(purchase.remaining_amount)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tutarı</label>
                <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPaymentModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}